/**
 * Admin Levels Hooks
 * 
 * Custom hooks for admin level operations.
 */

import * as React from "react";
import { adminService, type AdminLevelItem, type AdminLevelType, type CreateAdminLevelInput, type UpdateAdminLevelInput } from "@/services/adminService";
import { toast } from "sonner";

/**
 * Hook to fetch admin levels
 */
export function useAdminLevels(level?: AdminLevelType) {
  const [adminLevels, setAdminLevels] = React.useState<AdminLevelItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const fetchAdminLevels = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.fetchAdminLevels(level);
      setAdminLevels(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch admin levels";
      setError(errorMessage);
      console.error("Error fetching admin levels:", err);
    } finally {
      setLoading(false);
    }
  }, [level]);

  React.useEffect(() => {
    fetchAdminLevels();
  }, [fetchAdminLevels]);

  return { adminLevels, loading, error, fetchAdminLevels };
}

/**
 * Hook to create admin level
 */
export function useCreateAdminLevel() {
  const [loading, setLoading] = React.useState(false);

  const createAdminLevel = React.useCallback(async (input: CreateAdminLevelInput) => {
    setLoading(true);
    try {
      const adminLevel = await adminService.createAdminLevel(input);
      toast.success("Admin level berhasil dibuat", {
        description: `Admin level "${adminLevel.name}" telah dibuat`,
      });
      return adminLevel;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create admin level";
      toast.error("Gagal membuat admin level", {
        description: errorMessage,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createAdminLevel, loading };
}

/**
 * Hook to update admin level
 */
export function useUpdateAdminLevel() {
  const [loading, setLoading] = React.useState(false);

  const updateAdminLevel = React.useCallback(async (id: string, input: UpdateAdminLevelInput) => {
    setLoading(true);
    try {
      const adminLevel = await adminService.updateAdminLevel(id, input);
      toast.success("Admin level berhasil diupdate", {
        description: `Admin level "${adminLevel.name}" telah diperbarui`,
      });
      return adminLevel;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update admin level";
      toast.error("Gagal mengupdate admin level", {
        description: errorMessage,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateAdminLevel, loading };
}

/**
 * Hook to delete admin level
 */
export function useDeleteAdminLevel() {
  const [loading, setLoading] = React.useState(false);

  const deleteAdminLevel = React.useCallback(async (id: string) => {
    setLoading(true);
    try {
      await adminService.deleteAdminLevel(id);
      toast.success("Admin level berhasil dihapus");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to delete admin level";
      toast.error("Gagal menghapus admin level", {
        description: errorMessage,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteAdminLevel, loading };
}

